import React, { useCallback, useState, useEffect, useContext } from "react";
import {
  Segment,
  Table,
  Select,
  Loader,
  Button,
  Input,
  Popup,
  Icon,
} from "semantic-ui-react";
import { DateInput } from "semantic-ui-calendar-react";
import apis from "../../../../../../../apis";
import { getDateFormat } from "../../../../../../../common/date-format";
import { UsersContext } from "../../../../../../../common/UsersContext";
import {
  notEmptyValidator,
  regExpValidator,
  useFilters,
  usePagination,
} from "../../../../../../../hooks";
import SmallConfirmPopup from "../../../../../../../components/SmallConfirmPopup";
import { downloadCsv } from "../../../../../../../utils";
import * as toastr from "toastr";
import { logServerError } from "../../../../../../../common/logs";
import { API_NAMES } from "../../../Products/EVisa/constants/apiNames";
import ModalTable from "../../../../../../../components/common/ModalTable";
import moment from "moment";
import { useMemo } from "react";

const UserLogs = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const { allUsers } = useContext(UsersContext);
  const [isExporting, setIsExporting] = useState(false);

  const {
    items: logs,
    loadItems: loadLogs,
    isLoading,
    refreshItems: refreshLogs,
    Paginator,
  } = usePagination(apis.logs.getAllLogs, {
    clearOnLoad: false,
    initialLoad: false,
  });

  const { normalizedFilters, filters, setFormFilter, updateFilters } = useFilters(
    {
      visaType: "ds160",
    },
    {
      localStorageKey: "all-applicant-logs-filters",
      filterValidators: {
        applicantId: regExpValidator(/^.{24}$/),
        eventType: notEmptyValidator(),
        createdAt: notEmptyValidator(),
        visaType: notEmptyValidator(),
        userId: notEmptyValidator(),
        logType: notEmptyValidator(),
      },
      temporaryFilters: ["userId", "createdAt", "applicantId"],
    },
  );

  const exportUsersLogsToCsv = useCallback(async () => {
    try {
      const { data } = await apis.logs.exportEventLogsToCSV(normalizedFilters);
      downloadCsv(data, "User_logs");
      toastr.success("Successfully exported data.");
    } catch (err) {
      logServerError(err);
    }
    setIsExporting(false);
  }, [normalizedFilters]);

  useEffect(() => {
    loadLogs(1, normalizedFilters);
  }, [loadLogs, normalizedFilters]);

  const getCreatedAt = (from, to) => {
    if (!from && !to) {
      return "";
    }

    if (from && !to) {
      return {
        $gte: from,
      };
    }

    if (!from && to) {
      return {
        $lt: to,
      };
    }
    return { $gte: from, $lt: to };
  };

  const columns = useMemo(() => {
    const { logType } = filters;
    const isOutsideAppLog = logType === "outsideApplicant"

    return([
      {
        label: "Log type",
        prop: "logType",
        width: 2,
      },
      {
        label: "Event type",
        prop: "eventType",
        width: 2,
      },
      ...(!isOutsideAppLog ? [{
        label: "Visa Type",
        prop: "visaType",
        width: 2,
      }] : []),
      {
        label: "Agent",
        prop: "userId",
        width: 2,
        getValue: (fieldValue) => (fieldValue ? allUsers.find(agent => agent.id === fieldValue)?.fullName : "")
      },
      ...(!isOutsideAppLog ? [{
        label: "Applicant ID",
        prop: "applicantId",
        width: 2,
      }] : []),
    ])},[allUsers, filters]);

  const renderHeader = useCallback(() => {
    const visaTypeOptions = Object.keys(API_NAMES).map((type) => ({
      key: API_NAMES[type],
      text: API_NAMES[type],
      value: API_NAMES[type],
    }));

    const agentOptions = allUsers.map((agent) => ({
      key: agent.id,
      text: agent.fullName,
      value: agent.id,
    }));

    const logTypes = ["outsideApplicant", "insideApplicant"].map((type) => ({
      key: type,
      text: type,
      value: type,
    }))

    const { userId, eventType, applicantId, visaType, logType } = filters;
    const isOutsideAppLog = logType === "outsideApplicant"

    return (
      <div className="users-header">
        <div className="users-filters">
          <Select
            options={logTypes}
            placeholder="Select log type"
            name="logType"
            value={logType}
            onChange={(_, field) => {
              updateFilters({[field.name]: field.value, ...(field.value === "outsideApplicant" && {visaType: "", applicantId: ""})})
            }}
            clearable
          />
          <Input
            label="Event type"
            name="eventType"
            value={eventType}
            onChange={setFormFilter}
          />
          <DateInput
            name="dateFrom"
            dateFormat={"YYYY-MM-DD"}
            clearable={true}
            placeholder="Date from"
            maxDate={dateTo ? moment(dateTo).subtract(1, 'day') : undefined}
            value={dateFrom}
            popupPosition="top right"
            animation="none"
            duration={0}
            iconPosition="left"
            onChange={(_, field) => {
              const { value } = field;
              setDateFrom(value);
              setFormFilter(null, {
                name: "createdAt",
                value: getCreatedAt(value, dateTo),
              });
            }}
          />
          <DateInput
            name="dateTo"
            dateFormat={"YYYY-MM-DD"}
            clearable={true}
            placeholder="Date to"
            minDate={dateFrom ? moment(dateFrom).add(1, 'day') : undefined}
            value={dateTo}
            popupPosition="top right"
            animation="none"
            duration={0}
            iconPosition="left"
            onChange={(_, field) => {
              const { value } = field;
              setDateTo(value);
              setFormFilter(null, {
                name: "createdAt",
                value: getCreatedAt(dateFrom, value),
              });
            }}
          />
          {!isOutsideAppLog && <><Input
            label="Application ID"
            name="applicantId"
            value={applicantId}
            onChange={setFormFilter}
          />
          <Select
            options={visaTypeOptions}
            placeholder="Select visa type"
            name="visaType"
            value={visaType}
            onChange={setFormFilter}
            clearable
          /></>}
          <Select
            options={agentOptions}
            placeholder="Select agent"
            name="userId"
            value={userId}
            onChange={setFormFilter}
            search
            clearable
          />
          <Button onClick={refreshLogs}>
            {isLoading ? "Loading.." : "Refresh"}
          </Button>
          <Popup
            content={"Export"}
            trigger={
              <span>
                <SmallConfirmPopup
                  header="Are you sure you want to export user logs?"
                  content={<Button positive floated="right" content="Export" />}
                  callback={() => {
                    exportUsersLogsToCsv();
                  }}
                >
                  <Button
                    color="blue"
                    size="medium"
                    icon
                    onClick={async () => {
                      setIsExporting(true);
                      exportUsersLogsToCsv();
                    }}
                    disabled={isExporting}
                  >
                    <Icon name="download" />
                  </Button>
                </SmallConfirmPopup>
              </span>
            }
          />
        </div>
      </div>
    );
  }, [
    allUsers,
    dateFrom,
    dateTo,
    filters,
    isLoading,
    isExporting,
    refreshLogs,
    setFormFilter,
    exportUsersLogsToCsv,
    updateFilters,
  ]);

  return (
    <React.Fragment>
      {!logs ? (
        <Loader active />
      ) : (
        <Segment.Group>
          {renderHeader()}
          <Table celled striped fixed selectable className="user-table">
            <Table.Header>
              <Table.Row>
                {columns.map((column) => (
                  <Table.HeaderCell
                    key={column.prop}
                    width={column.width ? column.width : 1}
                  >
                    {column.label}
                  </Table.HeaderCell>
                ))}
                <Table.HeaderCell width={2}>Actions</Table.HeaderCell>
                <Table.HeaderCell width={2}>Created at</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {logs &&
                logs.map((log) => (
                  <Table.Row key={log._id}>
                    {columns.map((column) => (
                      <Table.Cell key={column.prop}>
                        {column.getValue
                          ? column.getValue(log[column.prop])
                          : log[column.prop]}
                      </Table.Cell>
                    ))}
                    <Table.Cell>
                      <ModalTable
                        headerText="List of actions made"
                        data={log.actions}
                        columns={[
                          {
                            label: "Property",
                            prop: "propertyName",
                            width: 1,
                          },
                          {
                            label: "Old value",
                            prop: "oldValue",
                            width: 2,
                          },
                          {
                            label: "New value",
                            prop: "newValue",
                            width: 2,
                          },
                        ]}
                        trigger={<Button>See actions</Button>}
                      />
                    </Table.Cell>
                    <Table.Cell>{getDateFormat(log.createdAt)}</Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan={columns.length}>
                  <Paginator />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </Segment.Group>
      )}
    </React.Fragment>
  );
};

export default UserLogs;
